import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAgents } from "@/api/queries/agents";
import { useCreateDirectConnection } from "@/api/mutations/connections";
import type { Agent } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceAgent: Agent;
}

// Admin-only Direct-Insert. Use-Case: Service-Agents die keinen Owner haben
// und damit den Owner-getriebenen Request-Flow nicht nutzen können.
export function DirectConnectionDialog({ open, onOpenChange, sourceAgent }: Props) {
  const agentsQuery = useAgents();
  const mutation = useCreateDirectConnection();

  const [targetId, setTargetId] = useState<string>("");
  const [serverError, setServerError] = useState<string | null>(null);

  const candidates = (agentsQuery.data ?? []).filter(
    (a) => a.id !== sourceAgent.id && a.revokedAt === null,
  );

  function handleSubmit() {
    if (!targetId) return;
    setServerError(null);
    mutation.mutate(
      { agentAId: sourceAgent.id, agentBId: targetId },
      {
        onSuccess: () => {
          toast.success("Connection direkt erstellt");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Direct fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="direct-connection-dialog">
        <DialogHeader>
          <DialogTitle>Direct-Connection (Admin)</DialogTitle>
          <DialogDescription>
            Überspringt den Request-Flow. Direkt active Connection ohne Approval.
            Use-Case: Service-Agents.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Agent A</Label>
            <div className="text-sm font-mono text-subtext0">{sourceAgent.id}</div>
          </div>

          <div className="space-y-1">
            <Label>Agent B</Label>
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger data-testid="direct-conn-target">
                <SelectValue placeholder="— Agent wählen" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} ({a.id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="direct-conn-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="direct-conn-cancel">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!targetId || mutation.isPending}
            data-testid="direct-conn-submit"
          >
            {mutation.isPending ? "..." : "Direct erstellen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
